import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const SmallBusinessPage = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'industry-overview',
        'dual-challenges',
        'holistic-approach',
        'margin-strategies',
        'value-proposition',
        'dashboard',
        'features'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
            {/* Hero */}
            <section className="sv-hero">
              <div className="sv-eyebrow">Small Business</div>
              <h1 className="sv-title">Enterprise-level returns management solutions for small businesses</h1>
              <p className="sv-subtitle">Globoflo provides enterprise-level returns management solutions for small businesses, with no minimum volume requirements and simple platform integration.</p>
            </section>

            {/* Industry Solutions */}
            <section className="sv-overview">
              <h2 className="sv-section-title">Industry Solutions</h2>
              <ul className="sv-bullets sv-list">
                <li>Store Closing – Quickly convert inventory to capital</li>
                <li>Charitable Donations – Tax savings & impact reporting</li>
                <li>Customer Returns – Efficient reverse logistics recovery</li>
                <li>Excess Inventory – Turn overstock into capital</li>
              </ul>
            </section>

            {/* Industry Overview */}
            <section id="industry-overview" className="sv-overview">
              <h2 className="sv-section-title">Industry Overview</h2>
              <p>Small businesses struggle with managing both customer returns and excess inventory due to limited resources and inconsistent volumes. Without enterprise solutions, they face challenges maintaining customer satisfaction while efficiently recovering value from returned merchandise and surplus stock across multiple sales channels.</p>
            </section>

            {/* Dual Inventory Challenges */}
            <section id="dual-challenges" className="sv-overview">
              <h2 className="sv-section-title">Dual Inventory Challenges for Small Business</h2>
              <p>Small businesses face a two-sided inventory management challenge:</p>
              
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Customer Returns</h3>
                <ul className="sv-bullets sv-list">
                  <li>16.5% average return rate for online purchases</li>
                  <li>Complex processing requirements with limited staff</li>
                  <li>Diminishing recovery value with processing delays</li>
                  <li>Rising customer expectations for frictionless experiences</li>
                </ul>
              </div>

              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Excess Inventory</h3>
                <ul className="sv-bullets sv-list">
                  <li>23% of small business inventory typically becomes excess or obsolete annually</li>
                  <li>Cash flow constraints from capital tied up in slow-moving stock</li>
                  <li>Limited warehouse space diverted to storing non-productive inventory</li>
                  <li>Restricted access to specialized liquidation channels available to larger businesses</li>
                </ul>
              </div>

              <p style={{ marginTop: '20px', color: '#475569' }}>These combined challenges create a significant burden, with small businesses reporting that inventory management issues consume an average of 18 hours weekly for owners and key staff—time that could otherwise drive growth.</p>
            </section>

            {/* Holistic Inventory Recovery Approach */}
            <section id="holistic-approach" className="sv-overview">
              <h2 className="sv-section-title">Holistic Inventory Recovery Approach</h2>
              
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Returns Processing</h3>
                <ul className="sv-bullets sv-list">
                  <li>Customer return authorization</li>
                  <li>Condition verification</li>
                  <li>Refund management</li>
                  <li>Resellable item recirculation</li>
                  <li>Non-resellable disposition</li>
                </ul>
              </div>

              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Excess Inventory</h3>
                <ul className="sv-bullets sv-list">
                  <li>Slow-moving SKU identification</li>
                  <li>Small-quantity liquidation</li>
                  <li>Bundling for improved margins</li>
                  <li>Channel-optimized redistribution</li>
                  <li>Age-based recovery strategies</li>
                </ul>
              </div>
            </section>

            {/* Margin Recovery Strategies */}
            <section id="margin-strategies" className="sv-overview">
              <h2 className="sv-section-title">Margin Recovery Strategies</h2>
              <p>Our solution transforms two traditional cost centers into value recovery opportunities through specialized channels typically inaccessible to small businesses:</p>
              
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Direct-to-Consumer Remarketing*</h3>
                <p style={{ color: '#475569', marginBottom: '12px' }}>Condition-appropriate products are directed to high-value secondary markets through anonymous channels that protect your brand positioning and pricing strategy.</p>
              </div>

              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Business-to-Business Distribution</h3>
                <p style={{ color: '#475569', marginBottom: '12px' }}>Small-quantity lots that would typically be unprofitable are consolidated with similar merchandise to create scale-efficient offerings for business buyers.</p>
              </div>

              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Component Recovery</h3>
                <p style={{ color: '#475569', marginBottom: '12px' }}>Products with viable components but unsellable as complete units undergo value-part harvesting to extract maximum residual value.</p>
              </div>

              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#0f172a' }}>Specialty Channel Matching</h3>
                <p style={{ color: '#475569', marginBottom: '12px' }}>Niche products find optimal placement through our network of 200+ specialty buyers seeking specific inventory types regardless of quantity.</p>
              </div>

              <p style={{ marginTop: '20px', color: '#475569', fontStyle: 'italic' }}>Small businesses implementing these strategies report an average 42% improvement in recovery value compared to traditional liquidation approaches.</p>
            </section>

            {/* Small Business Value Proposition */}
            <section id="value-proposition" className="sv-overview">
              <h2 className="sv-section-title">Small Business Value Proposition</h2>
              <p>Unlike enterprise-focused services that ignore small business needs, our solution delivers:</p>
              <ul className="sv-bullets sv-list">
                <li>No minimum quantity requirements – start with as few as 50 units monthly</li>
                <li>Combined processing for customer returns and excess inventory</li>
                <li>Simple per-item pricing with no hidden fees or surcharges</li>
                <li>Month-to-month service without long-term commitments</li>
                <li>Dedicated account manager familiar with small business challenges</li>
              </ul>
              <p style={{ marginTop: '20px', color: '#475569' }}>This approach achieves enterprise-grade inventory management with an average 87% reduction in administrative time and a 3.4x return on service costs through improved recovery values and operational efficiencies.</p>
            </section>

            {/* Inventory Intelligence Dashboard */}
            <section id="dashboard" className="sv-overview">
              <h2 className="sv-section-title">Inventory Intelligence Dashboard</h2>
              <p>Small businesses lack the analytics resources of larger companies, yet need data-driven insights to optimize inventory decisions. Our simplified dashboard delivers:</p>
              <ul className="sv-bullets sv-list">
                <li>Unified view of returns and excess inventory status</li>
                <li>Performance comparison across sales channels</li>
                <li>Product-level recovery rate analysis</li>
                <li>Seasonality insights for improved purchasing</li>
                <li>Automated slow-moving inventory alerts</li>
                <li>One-click disposition decisions</li>
              </ul>
              <p style={{ marginTop: '20px', color: '#475569' }}>This purpose-built interface eliminates the complexity of enterprise systems while delivering the actionable intelligence small businesses need to make informed inventory decisions.</p>
            </section>

            {/* Features */}
            <section id="features" className="sv-features">
              <h2 className="sv-section-title">Features</h2>
              <div className="sv-features-grid">
                <div className="sv-feature">
                  <h4>Branded Return Portal</h4>
                  <p>Customized user experience that matches your website design.</p>
                </div>
                <div className="sv-feature">
                  <h4>Excess Inventory Liquidation</h4>
                  <p>Multiple channels for optimal placement of slow-moving stock.</p>
                </div>
                <div className="sv-feature">
                  <h4>Recovery Channel Access</h4>
                  <p>Direct connections to specialized buyers typically unavailable to small businesses.</p>
                </div>
                <div className="sv-feature">
                  <h4>Condition Grading</h4>
                  <p>Professional assessment ensuring optimal disposition and value recovery.</p>
                </div>
                <div className="sv-feature">
                  <h4>Inventory Analytics</h4>
                  <p>Insights revealing opportunity areas across returns and excess stock.</p>
                </div>
                <div className="sv-feature">
                  <h4>Flexible Billing</h4>
                  <p>Multiple payment options including per-item and subscription models.</p>
                </div>
              </div>
            </section>

            {/* Four Pillars */}
            <FourPillars />

            {/* CTA */}
            <section className="sv-cta">
              <div className="sv-cta-inner">
                <h3>Get in touch</h3>
                <p>Connect with inventory experts who transform your returns and excess stock into recovered revenue. Custom solutions await.</p>
                <div className="sv-cta-actions">
                  <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
                  <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">Small Business</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'industry-overview' ? 'active' : ''}
                    onClick={() => scrollToSection('industry-overview')}
                  >
                    Industry Overview
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'dual-challenges' ? 'active' : ''}
                    onClick={() => scrollToSection('dual-challenges')}
                  >
                    Dual Inventory Challenges
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'holistic-approach' ? 'active' : ''}
                    onClick={() => scrollToSection('holistic-approach')}
                  >
                    Holistic Inventory Recovery Approach
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'margin-strategies' ? 'active' : ''}
                    onClick={() => scrollToSection('margin-strategies')}
                  >
                    Margin Recovery Strategies
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'value-proposition' ? 'active' : ''}
                    onClick={() => scrollToSection('value-proposition')}
                  >
                    Small Business Value Proposition
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'dashboard' ? 'active' : ''}
                    onClick={() => scrollToSection('dashboard')}
                  >
                    Inventory Intelligence Dashboard
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'features' ? 'active' : ''}
                    onClick={() => scrollToSection('features')}
                  >
                    Features
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default SmallBusinessPage;


